import { useMemo, useEffect, useCallback, useState } from 'react';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import { toastr } from 'react-redux-toastr';
import { useFlags } from 'launchdarkly-react-client-sdk';
import isNil from 'lodash/isNil';

// Components
import {
  Typography,
  Box,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Banner } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  Modal,
  ModalConfirm,
} from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import {
  PlusIcon,
  PencilIcon,
  AlertInfoIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';

// Hooks
import useReactHookFormProps from '../../../hooks/useReactHookFormProps';
import usePermissionsHook from '../../../components/ElementWithPermissions/usePermissionsHook';
import {
  useEditHousingAssistanceVoucher,
  useCreateHousingAssistanceVoucher,
  useDeleteHousingAssistanceVoucher,
} from '../hooks.useMutations';

// Others
import messages from '../messages';
import moment from 'moment';
import InfoContainer from '../../../components/InfoContainer';

const confirmModalPropsDiscardChanges = ({
  closeConfirmModal,
  handleClose,
  openConfirm,
}) => ({
  title: 'Discard Changes?',
  onClose: closeConfirmModal,
  open: openConfirm,
  actionsProps: [
    {
      children: 'Cancel',
      onClick: closeConfirmModal,
    },
    {
      children: 'Discard Changes',
      onClick: () => {
        handleClose();
        closeConfirmModal();
      },
    },
  ],
});

const ManageHousingAssistanceVoucherModals = ({
  organizationId,
  propertyId,
  householdId,
  refetchVouchers,
  stage,
  householdMoveInDate,
  transferDate,
  intl,
  modalState,
  modalType,
  open,
  openConfirm,
  openConfirmModal,
  handleClose,
  closeConfirmModal,
}) => {
  const { housingVoucherCoverRentSwitch } = useFlags();
  const hasSavePostAdjustmentsPermission = usePermissionsHook({
    scope: ['housing-assistance-save-post-adjustments'],
  });
  const {
    mutate: createHousingAssistanceVoucherMutate,
    isLoading: createHousingAssistanceVoucherMutationIsLoading,
  } = useCreateHousingAssistanceVoucher({
    organizationId,
    propertyId,
    householdId,
    options: {
      onSuccess: (res) => {
        toastr.success('Success', res.message);
        handleClose();
        refetchVouchers();
      },
      onError: (e) => {
        toastr.error('Error', e);
      },
    },
  });
  const clonedMoveInDate = householdMoveInDate
    ? moment(householdMoveInDate).clone()
    : null;
  const clonedTransferDate = !isNil(transferDate)
    ? moment(transferDate).clone()
    : null;
  const {
    mutate: editHousingAssistanceVoucherMutate,
    isLoading: editHousingAssistanceVoucherMutationIsLoading,
  } = useEditHousingAssistanceVoucher({
    organizationId,
    propertyId,
    householdId,
    options: {
      onSuccess: (res) => {
        if (res.data?.transactions) {
          toastr.info(
            'Info',
            'Transactions have been created for this voucher',
          );
        }
        toastr.success('Success', res.message);
        handleClose();
        refetchVouchers();
      },
      onError: (e) => {
        toastr.error('Error', e);
      },
    },
  });

  const {
    mutate: deleteHousingAssistanceVoucherMutate,
    isLoading: deleteHousingAssistanceVoucherMutationIsLoading,
  } = useDeleteHousingAssistanceVoucher({
    organizationId,
    propertyId,
    householdId,
    options: {
      onSuccess: (res) => {
        toastr.success('Success', res.message);
        closeConfirmModal();
        refetchVouchers();
      },
      onError: (e) => {
        toastr.error('Error', e);
      },
    },
  });

  const ReactHookFormProps = useReactHookFormProps({});
  const { reset, getValues, isValid, isDirty, watch } = ReactHookFormProps;
  const voucherStartDate = watch('voucherStartDate');
  const isPastDate = useMemo(() => {
    return (
      voucherStartDate && moment(voucherStartDate).isBefore(moment(), 'day')
    );
  }, [voucherStartDate]);

  useEffect(() => {
    const {
      voucherSource,
      voucherAmount,
      voucherStartDate,
      voucherEndDate,
      coversRentChanges,
    } = modalState?.voucher || {};

    const baseEditValues = {
      voucherSource: voucherSource || '',
      voucherAmount: voucherAmount || '',
      voucherStartDate: voucherStartDate ? moment(voucherStartDate) : null,
      voucherEndDate: voucherEndDate ? moment(voucherEndDate) : null,
    };

    const baseCreateValues = {
      voucherSource: '',
      voucherAmount: '',
      voucherStartDate: stage === 'applicant' ? clonedMoveInDate : null,
      voucherEndDate: null,
    };

    if (housingVoucherCoverRentSwitch) {
      baseEditValues.coversRentChanges = coversRentChanges || false;
      baseCreateValues.coversRentChanges = false;
    }

    const defaultValues = {
      editVoucher: baseEditValues,
      createVoucher: baseCreateValues,
    }[modalType];

    reset(defaultValues);
  }, [reset, modalType, modalState, stage, housingVoucherCoverRentSwitch]);

  /**
   * Returns the form fields to display on the create/edit modal
   * -
   * @param {*} param0
   * @returns
   */
  const formFields = useCallback(
    ({ intl }) => {
      let disabled = false;
      const shouldDisableDate = (date) => {
        if (date) {
          const dayOfMonth = new Date(date).getDate();
          const dayOfScheduledMoveIn = new Date(clonedMoveInDate).getDate();
          const monthOfScheduledMoveIn = new Date(clonedMoveInDate).getMonth();
          const dayOfTransfer = clonedTransferDate
            ? new Date(clonedTransferDate).getDate()
            : null;
          const monthOfTransfer = clonedTransferDate
            ? new Date(clonedTransferDate).getMonth()
            : null;
          const monthOfDate = new Date(date).getMonth();
          return (
            dayOfMonth !== 1 &&
            !(
              (dayOfMonth === dayOfScheduledMoveIn &&
                monthOfScheduledMoveIn === monthOfDate) ||
              (dayOfTransfer === dayOfMonth && monthOfTransfer === monthOfDate)
            )
          );
        }
      };

      return [
        {
          variant: 'text',
          name: 'voucherSource',
          label: <FormattedMessage {...messages.voucherSource} />,
          rules: {
            required: true,
          },
          disabled,
          GridProps: {
            xs: 6,
          },
        },
        {
          variant: 'number',
          name: 'voucherAmount',
          label: <FormattedMessage {...messages.voucherAmount} />,
          adornment: '$',
          rules: {
            required: true,
            min: 0,
            step: 0.01,
          },
          disabled: false,
          GridProps: {
            xs: 6,
          },
        },
        {
          variant: 'custom',
          component: (
            <InfoContainer
              title={
                <FormattedMessage {...messages.editVoucherDatesInfoTitle} />
              }
              content={
                <FormattedHTMLMessage {...messages.editVoucherStartDateInfo} />
              }
            />
          ),
          GridProps: {
            xs: 12,
          },
        },
        {
          variant: 'date',
          name: 'voucherStartDate',
          label: <FormattedMessage {...messages.voucherStartDate} />,
          views: ['month', 'year'],
          format: 'YYYY-MM-DD',
          placeholder: 'YYYY-MM-DD',
          minDate:
            modalType === 'createVoucher' && stage !== 'applicant'
              ? moment().add(1, 'month').startOf('month')
              : modalType === 'createVoucher' && stage === 'applicant'
              ? moment.max(moment(), moment(householdMoveInDate))
              : moment(householdMoveInDate),
          defaultCalendarMonth: moment().add(1, 'month'),
          shouldDisableDate: (date) => shouldDisableDate(date),
          rules: {
            required: true,
            validate: (date) => {
              let isValid = false;
              if (date) {
                isValid = !shouldDisableDate(date);
              }
              if (!isValid) {
                return intl.formatMessage({
                  id: 'App.HousingAssistanceVouchers.StartDateError',
                });
              }
              return isValid;
            },
          },
          disabled: false,
          GridProps: {
            xs: 6,
          },
        },
        {
          variant: 'date',
          name: 'voucherEndDate',
          label: <FormattedMessage {...messages.voucherEndDate} />,
          views: ['month', 'year'],
          format: 'YYYY-MM-DD',
          placeholder: 'YYYY-MM-DD',
          defaultCalendarMonth: moment().add(1, 'month'),
          minDate: moment(householdMoveInDate),
          shouldDisableDate: (date) => {
            if (date) {
              const _date = new Date(date);
              const lastDay = new Date(
                _date.getFullYear(),
                _date.getMonth() + 1,
                0,
              ).getDate();
              return _date.getDate() !== lastDay;
            }
          },
          rules: {
            required: false,
            validate: (date) => {
              let isValid = true;
              if (date) {
                const _date = new Date(date);
                const lastDay = new Date(
                  _date.getFullYear(),
                  _date.getMonth() + 1,
                  0,
                ).getDate();
                isValid = _date.getDate() === lastDay;
              }
              if (!isValid) {
                return intl.formatMessage({
                  id: 'App.HousingAssistanceVouchers.EndDateError',
                });
              }
              return isValid;
            },
          },
          disabled,
          GridProps: {
            xs: 6,
          },
        },
        ...(housingVoucherCoverRentSwitch
          ? [
              {
                variant: 'switch',
                name: 'coversRentChanges',
                label: (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormattedMessage {...messages.coversRentIncreases} />
                    <Tooltip
                      variant={'light'}
                      placement="top"
                      title={
                        <Typography color={'text.secondary'}>
                          <FormattedHTMLMessage
                            {...messages.coversRentIncreasesTooltip}
                          />
                        </Typography>
                      }
                    >
                      <AlertInfoIcon />
                    </Tooltip>
                  </Box>
                ),
                helperText: (
                  <FormattedMessage
                    {...messages.coversRentIncreasesHelperText}
                  />
                ),
                rules: {
                  required: false,
                },
                disabled: false,
                GridProps: {
                  alignItems: 'flex-end',
                  display: 'flex',
                  xs: 12,
                },
              },
            ]
          : []),
      ];
    },
    // Add dependencies in case the fields need to change based on some condition for the future
    [householdMoveInDate, modalType, stage, housingVoucherCoverRentSwitch],
  );

  /**
   * Helper function to build payload from form values
   * @returns {Object} voucher payload
   */
  const buildVoucherPayload = useCallback(() => {
    const values = getValues();
    const payload = {
      voucherSource: values.voucherSource,
      voucherAmount: values.voucherAmount,
      voucherStartDate: values.voucherStartDate,
      voucherEndDate: values.voucherEndDate,
    };

    if (housingVoucherCoverRentSwitch) {
      payload.coversRentChanges = values.coversRentChanges || false;
    }

    return payload;
  }, [getValues, housingVoucherCoverRentSwitch]);
  const [omitAdjustments, setOmitAdjustments] = useState(false);

  /**
   * Helper function to handle edit voucher action
   * @param {boolean} omitAdjustments - Whether to post adjustments or not
   */
  const handleEditVoucher = useCallback(
    (omitAdjustments = false) => {
      const housingAssistanceVoucherId = modalState?.voucher?.id;
      const payload = buildVoucherPayload();
      setOmitAdjustments(omitAdjustments);
      editHousingAssistanceVoucherMutate({
        housingAssistanceVoucherId,
        payload: omitAdjustments
          ? { ...payload, omitAdjustments: true }
          : { ...payload, omitAdjustments: false },
      });
      window.dispatchEvent(new CustomEvent('VOUCHERS_UPDATED'));
    },
    [buildVoucherPayload, modalState, editHousingAssistanceVoucherMutate],
  );

  /**
   * Helper function to handle create voucher action
   */
  const handleCreateVoucher = useCallback(() => {
    createHousingAssistanceVoucherMutate({
      payload: buildVoucherPayload(),
    });
  }, [buildVoucherPayload, createHousingAssistanceVoucherMutate]);

  const { confirmModalProps, modalProps, formProps } = useMemo(() => {
    const props = {
      modalProps: {},
      formProps: {},
      confirmModalProps: {},
    };

    switch (modalType) {
      case 'createVoucher':
        props.modalProps = {
          title: <FormattedMessage {...messages.addNewVoucher} />,
          icon: <PlusIcon />,
          ContentProps: {
            sx: {
              maxWidth: '600px',
            },
          },
          actionsProps: [
            {
              children: 'Cancel',
              onClick: isDirty ? openConfirmModal : handleClose,
            },
            {
              children: 'Save & Post Adjustments',
              disabled: true, // This button is never enabled to used for now
            },
            {
              children: ' Save ', // adding space so it doesn't add the icon
              submitButton: true,
              isSubmitting: createHousingAssistanceVoucherMutationIsLoading,
              disabled: !isValid || !isDirty,
              onClick: handleCreateVoucher,
            },
          ],
        };
        props.formProps = {
          fields: formFields({ intl }),
        };
        props.confirmModalProps = confirmModalPropsDiscardChanges({
          closeConfirmModal,
          handleClose,
          openConfirm,
        });

        break;
      case 'editVoucher':
        props.modalProps = {
          title: <FormattedMessage {...messages.editVoucher} />,
          icon: <PencilIcon />,
          ContentProps: {
            sx: {
              maxWidth: '600px',
            },
          },
          actionsProps: [
            {
              children: 'Cancel',
              onClick: isDirty ? openConfirmModal : handleClose,
            },
            {
              children: 'Save & Post Adjustments',
              submitButton: true,
              // Variant isn't a thing in the actionsProps, so we use sx to style it
              sx: {
                backgroundColor: 'secondary.main',
                '&:hover': {
                  backgroundColor: 'secondary.dark',
                },
              },
              isSubmitting:
                editHousingAssistanceVoucherMutationIsLoading &&
                !omitAdjustments,
              disabled:
                !hasSavePostAdjustmentsPermission ||
                !isValid ||
                !isDirty ||
                !isPastDate ||
                editHousingAssistanceVoucherMutationIsLoading,
              ...(!hasSavePostAdjustmentsPermission && {
                tooltip: 'You do not have permission to perform this action',
              }),
              onClick: () => handleEditVoucher(false),
            },
            {
              children: ' Save ', // adding space so it doesn't add the icon
              submitButton: true,
              isSubmitting:
                editHousingAssistanceVoucherMutationIsLoading &&
                omitAdjustments,
              disabled:
                !isValid ||
                !isDirty ||
                editHousingAssistanceVoucherMutationIsLoading,
              onClick: () => handleEditVoucher(true),
            },
          ],
        };
        props.formProps = {
          fields: formFields({ intl }),
        };
        props.confirmModalProps = confirmModalPropsDiscardChanges({
          closeConfirmModal,
          handleClose,
          openConfirm,
        });

        break;
      case 'deleteVoucher':
        props.modalProps = {};
        props.formProps = {};
        props.confirmModalProps = {
          title: <FormattedMessage {...messages.deleteVoucher} />,
          children: (
            <Typography>
              <FormattedMessage {...messages.deleteVoucherConfirmation} />
            </Typography>
          ),
          actionsProps: [
            {
              children: 'Cancel',
              onClick: closeConfirmModal,
            },
            {
              children: 'Yes',
              submitButton: true,
              isSubmitting: deleteHousingAssistanceVoucherMutationIsLoading,
              onClick: () => {
                deleteHousingAssistanceVoucherMutate({
                  housingAssistanceVoucherId: modalState?.voucher?.id,
                });
              },
            },
          ],
        };
        break;

      default:
        break;
    }

    return props;
  }, [
    modalType,
    isDirty,
    openConfirmModal,
    handleClose,
    createHousingAssistanceVoucherMutationIsLoading,
    isValid,
    handleCreateVoucher,
    formFields,
    intl,
    closeConfirmModal,
    openConfirm,
    editHousingAssistanceVoucherMutationIsLoading,
    isPastDate,
    deleteHousingAssistanceVoucherMutationIsLoading,
    handleEditVoucher,
    deleteHousingAssistanceVoucherMutate,
    modalState?.voucher?.id,
    omitAdjustments,
    hasSavePostAdjustmentsPermission,
  ]);

  return (
    <>
      <Modal open={open} onClose={handleClose} {...modalProps}>
        <Form ReactHookFormProps={ReactHookFormProps} {...formProps} />
        <Banner
          icon={<AlertInfoIcon />}
          color="blue"
          text={<FormattedHTMLMessage {...messages.editVoucherInfo} />}
          hasBorder={false}
          TypographyProps={{
            noWrap: false,
          }}
        />
      </Modal>
      <ModalConfirm open={openConfirm} {...confirmModalProps} />
    </>
  );
};

export default ManageHousingAssistanceVoucherModals;
